// Arrastar para reordenar grupos e itens do orçamento (Rafael, 07/10) — peças de @dnd-kit.
// Regra pura (fixos, remapear expandidos) em ordemOrcamentoPolicy.js.
//
// Cada GRUPO vira um <tbody> próprio (cabeçalho + itens + subtotal andam juntos) e cada ITEM é o
// seu <tr>. Os itens têm um DndContext por grupo, então um item nunca sai do seu grupo.
// O arraste só começa pela alça (⋮⋮) — inputs e selects da linha continuam funcionando.
import React from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaGripVertical } from 'react-icons/fa';

// Só eixo vertical (o @dnd-kit/modifiers não está instalado).
const somenteVertical = ({ transform }) => ({ ...transform, x: 0 });

export function ListaOrdenavel({ ids, onMover, children }) {
    const sensores = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const aoSoltar = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const de = ids.indexOf(active.id);
        const para = ids.indexOf(over.id);
        if (de < 0 || para < 0) return;
        onMover(de, para);
    };
    return (
        <DndContext sensors={sensores} collisionDetection={closestCenter} modifiers={[somenteVertical]} onDragEnd={aoSoltar}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
        </DndContext>
    );
}

function estiloArraste(transform, transition, isDragging) {
    return {
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 20, opacity: 0.9, boxShadow: '0 6px 18px rgba(15, 23, 42, 0.18)' } : {}),
    };
}

// Grupo inteiro: children recebe a alça para pôr na célula de ações.
export function GrupoOrdenavel({ id, disabled = false, children }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    return (
        <tbody ref={setNodeRef} style={estiloArraste(transform, transition, isDragging)} data-grupo-ordenavel={id}>
            {children({ attributes, listeners, setActivatorNodeRef, disabled })}
        </tbody>
    );
}

// Uma linha de item: repassa as props do <tr> original e entrega a alça ao children.
export function LinhaOrdenavel({ id, disabled = false, style, children, ...props }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    return (
        <tr ref={setNodeRef} {...props} style={{ ...style, ...estiloArraste(transform, transition, isDragging) }}>
            {children({ attributes, listeners, setActivatorNodeRef, disabled })}
        </tr>
    );
}

export function AlcaArrastar({ alca, size = 13, titulo = 'Arraste para mudar a posição', tituloFixo = 'Posição fixa' }) {
    if (!alca) return null;
    if (alca.disabled) {
        return (
            <span className="fin-pp__btn fin-pp__btn--sm" title={tituloFixo} aria-hidden="true"
                style={{ opacity: 0.25, cursor: 'not-allowed', paddingLeft: 4, paddingRight: 4 }}>
                <FaGripVertical size={size} />
            </span>
        );
    }
    return (
        <button type="button" ref={alca.setActivatorNodeRef} {...alca.attributes} {...alca.listeners}
            className="fin-pp__btn fin-pp__btn--sm" title={titulo} data-alca-arrastar
            style={{ cursor: 'grab', touchAction: 'none', paddingLeft: 4, paddingRight: 4 }}>
            <FaGripVertical size={size} />
        </button>
    );
}
